import InfoOutlined from '@mui/icons-material/InfoOutlined';
import { Box, Grid, Tooltip } from '@mui/material';
import classNames from 'classnames';
import React from 'react';
import QuestionType from 'store/enums/QuestionType';
import { ApplicationContentSectionView } from 'store/types/ApplicationContent';
import { ApplicationQuestionView } from 'store/types/ApplicationQuestion';
import { getQuestionFieldName, getReviewSectionId, getSectionId } from 'util/Application';
import { defaultGridContainerProps, defaultGridItemProps } from 'util/Layout';
import AddressQuestionFormItem from './AddressQuestionFormItem';
import ChecklistQuestionFormItem from './ChecklistQuestionFormItem';
import DateQuestionFormItem from './DateQuestionFormItem';
import DropdownCustomListQuestionFormItem from './DropdownCustomListQuestionFormItem';
import EmailQuestionFormItem from './EmailQuestionFormItem';
import FileQuestionFormItem from './FileQuestionFormItem';
import MultilineTextQuestionFormItem from './MultilineTextQuestionFormItem';
import MultiSelectQuestionFormItem from './MultiSelectQuestionFormItem';
import PhoneQuestionFormItem from './PhoneQuestionFormItem';
import RadioQuestionFormItem from './RadioQuestionFormItem';
import ReviewQuestionFormItem from './ReviewQuestionFormItem';
import SelectQuestionFormItem from './SelectQuestionFormItem';
import SublistQuestionFormItem from './SublistQuestionFormItem';
import TextQuestionFormItem from './TextQuestionFormItem';

import commonStyles from 'styles/common.module.scss';
import styles from './ApplicationQuestionsFormSection.module.scss';

interface ApplicationQuestionsFormSectionProps {
  sections?: ApplicationContentSectionView[];
  isReviewForm?: boolean;
  disabled?: boolean;
  applicationId?: string;
}

const getQuestionInput = (
  { type, fieldName, required, options, sublistQuestions, fileRequired, files, review, id }: ApplicationQuestionView,
  disabled?: boolean,
  isReviewForm?: boolean,
  applicationId?: string
): React.ReactNode => {
  const inputProps = {
    fieldName,
    required,
    disabled,
    options,
    sublistQuestions,
    fileRequired,
    files,
    review,
    applicationId,
    id,
  };

  if (isReviewForm) {
    return <ReviewQuestionFormItem {...inputProps} />;
  } else {
    switch (type) {
      case QuestionType.Text:
        return <TextQuestionFormItem {...inputProps} />;
      case QuestionType.Checkbox:
        return <ChecklistQuestionFormItem {...inputProps} />;
      case QuestionType.Radio:
        return <RadioQuestionFormItem {...inputProps} />;
      case QuestionType.Dropdown:
        return <SelectQuestionFormItem {...inputProps} />;
      case QuestionType.File:
        return <FileQuestionFormItem {...inputProps} />;
      case QuestionType.SubList:
        return <SublistQuestionFormItem {...inputProps} />;
      case QuestionType.Date:
        return <DateQuestionFormItem {...inputProps} />;
      case QuestionType.MultilineText:
        return <MultilineTextQuestionFormItem {...inputProps} />;
      case QuestionType.DropdownCustomList:
        return <DropdownCustomListQuestionFormItem {...inputProps} />;
      case QuestionType.Phone:
        return <PhoneQuestionFormItem {...inputProps} />;
      case QuestionType.Email:
        return <EmailQuestionFormItem {...inputProps} />;
      case QuestionType.Address:
        return <AddressQuestionFormItem {...inputProps} />;
      case QuestionType.MultiSelectDropDown:
        return <MultiSelectQuestionFormItem {...inputProps} />;
      default:
        return <></>;
    }
  }
};

const getQuestionTitle = (title: string, index: number, required: boolean): string =>
  `Q${++index}. ${title}${required ? '*' : ''}`;

const ApplicationQuestionsFormSection: React.FunctionComponent<ApplicationQuestionsFormSectionProps> = ({
  sections = [],
  disabled = false,
  isReviewForm = false,
  applicationId,
}) => {
  return (
    <>
      {sections.map(({ title, description, questions = [] }, index) => {
        const sectionId = isReviewForm ? getReviewSectionId(index) : getSectionId(index);

        return (
          <section key={sectionId} id={sectionId}>
            <Grid {...defaultGridContainerProps}>
              <Grid {...defaultGridItemProps}>
                <h2 className={classNames({ [styles.disabled]: disabled })}>{title}</h2>
                <p
                  className={classNames(commonStyles.text, { [styles.disabled]: disabled })}
                  dangerouslySetInnerHTML={{ __html: description }}
                />
              </Grid>
              {questions.map((question, questionIndex) => {
                const { id, content, required } = question;
                const questionTitle: string = getQuestionTitle(
                  isReviewForm ? 'Requirement Met?' : question.title,
                  questionIndex,
                  required
                );

                return (
                  <React.Fragment key={getQuestionFieldName(id)}>
                    <Grid {...defaultGridItemProps}>
                      <Box display="flex" alignItems="center" gap={1}>
                        <h4
                          id={getQuestionFieldName(id)}
                          className={classNames(styles.label, { [styles.disabled]: disabled })}
                        >
                          {questionTitle}
                        </h4>
                        {question.additionalInfo && (
                          <Tooltip
                            arrow={true}
                            placement={'top'}
                            classes={{ tooltip: styles.tooltipContent }}
                            title={
                              <p
                                className={commonStyles.text}
                                dangerouslySetInnerHTML={{ __html: question.additionalInfo || 'More info' }}
                              />
                            }
                          >
                            <span className={styles.tooltip} style={{ marginTop: '-20px' }}>
                              <InfoOutlined color={'info'} className={styles.icon} />
                            </span>
                          </Tooltip>
                        )}
                      </Box>

                      {!isReviewForm && (
                        <p
                          dangerouslySetInnerHTML={{ __html: content }}
                          className={classNames({ [styles.disabled]: disabled })}
                        />
                      )}
                      {getQuestionInput(question, disabled, isReviewForm, applicationId)}
                    </Grid>
                  </React.Fragment>
                );
              })}
            </Grid>
          </section>
        );
      })}
    </>
  );
};
export default ApplicationQuestionsFormSection;
